import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { LibraryManager, ResourceType } from 'libs.library_manager';
import { useLocalizeMessage, Table, Badge, H5, Text, Spinner } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { getStatusBadgeVariant, getStatusLabel } from '../../../charts/utils';
import { useDashboards } from '../../hooks';
export const SelectCharts = ({ cubeConfigKey, selectedCharts, onSelectionChange, existingChartIds = [], }) => {
    const translate = useLocalizeMessage();
    const { fetchPublishedCharts } = useDashboards();
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const libraryClientRef = useRef(libraryClient);
    libraryClientRef.current = libraryClient;
    const [charts, setCharts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const chartsRef = useRef([]);
    const selectedChartsRef = useRef(selectedCharts);
    const onSelectionChangeRef = useRef(onSelectionChange);
    useEffect(() => {
        selectedChartsRef.current = selectedCharts;
    }, [selectedCharts]);
    useEffect(() => {
        onSelectionChangeRef.current = onSelectionChange;
    }, [onSelectionChange]);
    const existingChartIdsRef = useRef(existingChartIds);
    existingChartIdsRef.current = existingChartIds;
    useEffect(() => {
        if (!cubeConfigKey) {
            return;
        }
        const loadCharts = async () => {
            setIsLoading(true);
            try {
                const result = await fetchPublishedCharts(cubeConfigKey);
                // Fetch any existing dashboard charts that aren't in the published list
                const publishedIds = new Set(result.map((c) => c.id));
                const missingIds = existingChartIdsRef.current.filter((id) => !publishedIds.has(id));
                let merged = result;
                if (missingIds.length > 0) {
                    const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
                    const fetched = await Promise.all(missingIds.map(async (id) => {
                        try {
                            const res = await libraryManager.getResourceById(id, ResourceType.REPORTING_CHART);
                            return res;
                        }
                        catch {
                            return undefined;
                        }
                    }));
                    const extras = fetched.filter((c) => c != null);
                    merged = [...result, ...extras];
                }
                setCharts(merged);
                chartsRef.current = merged;
                // Reconcile: match existing selections against the merged list.
                // First try exact ID match; fall back to key match (handles versioned charts).
                const availableById = new Map(merged.map((c) => [c.id, c]));
                const availableByKey = new Map(merged.map((c) => [c.key, c]));
                const reconciled = [];
                for (const sc of selectedChartsRef.current) {
                    const byId = availableById.get(sc.resourceId);
                    if (byId) {
                        reconciled.push(sc);
                        continue;
                    }
                    const byKey = availableByKey.get(sc.resourceKey);
                    if (byKey) {
                        const chartData = byKey.data;
                        reconciled.push({
                            resourceId: byKey.id,
                            resourceKey: byKey.key,
                            name: byKey.name,
                            type: chartData?.type || sc.type,
                            title: chartData?.title || byKey.name || sc.title,
                        });
                    }
                }
                if (reconciled.length !== selectedChartsRef.current.length ||
                    reconciled.some((r, i) => r.resourceId !== selectedChartsRef.current[i].resourceId)) {
                    onSelectionChangeRef.current(reconciled);
                }
            }
            catch {
                setCharts([]);
                chartsRef.current = [];
            }
            finally {
                setIsLoading(false);
            }
        };
        loadCharts();
    }, [cubeConfigKey, fetchPublishedCharts]);
    const selectedIds = useMemo(() => selectedCharts.map((c) => c.resourceId), [selectedCharts]);
    const handleSelectionChange = useCallback((ids) => {
        const stringIds = ids.map(String);
        const chartInfos = stringIds.map((id) => {
            const chart = chartsRef.current.find((c) => c.id === id);
            const chartData = chart?.data;
            return {
                resourceId: chart?.id || id,
                resourceKey: chart?.key || '',
                name: chart?.name || '',
                type: chartData?.type || 'table',
                title: chartData?.title || chart?.name || '',
            };
        });
        onSelectionChange(chartInfos);
    }, [onSelectionChange]);
    const columns = useMemo(() => [
        {
            header: translate('Name'),
            accessorKey: 'name',
            cell: ({ row }) => (_jsx(Text, { size: 'sm', className: 'font-medium', children: row.original.name })),
        },
        {
            header: translate('Description'),
            accessorKey: 'description',
            accessorFn: (row) => row.metadata?.description || '',
        },
        {
            header: translate('Version'),
            accessorKey: 'version',
            cell: ({ row }) => _jsx(Badge, { label: `V${row.original.version}` }),
            size: 120,
        },
        {
            header: translate('Status'),
            accessorKey: 'status',
            cell: ({ row }) => (_jsx(Badge, { variant: getStatusBadgeVariant(row.original.status), label: getStatusLabel(row.original.status, translate) })),
            size: 120,
        },
    ], [translate]);
    if (!cubeConfigKey) {
        return (_jsx("div", { className: 'flex items-center justify-center size-full p-8', children: _jsx(Text, { size: 'sm', className: 'text-neutral-500', children: translate('Please select a dataset first.') }) }));
    }
    if (isLoading) {
        return (_jsx("div", { className: 'flex items-center justify-center size-full p-8', children: _jsx(Spinner, {}) }));
    }
    return (_jsxs("div", { className: 'flex flex-col size-full p-4', children: [_jsx(H5, { className: 'mb-4', children: translate('Select charts to bring to your dashboard') }), _jsx(Table, { columns: columns, data: charts, isLoading: isLoading, hasPagination: true, initialPageSize: 10, paginationNavigationScreenReaderLabel: translate('Pagination navigation'), isSelectable: true, getRowId: (row) => row.id, selectedIds: selectedIds, onRowSelectionChange: handleSelectionChange, useInternalSearch: true, labelNoResults: translate('No published charts found for this dataset.') })] }));
};
